import React from 'react';
import { StyleSheet } from 'react-native';

import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';

import { useCarouselCardSize } from '../../hooks/use-desktop-carousel';
import { CARD_GAP } from '../../utils/constants';
import { tiledGroupKey, toCheckerboardGroups } from '../../utils/tiled-groups';
import { CasinoLayoutProps } from '../../utils/utils';
import { LayoutHeader } from './LayoutHeader';
import { TiledGroupCard } from './TiledGroupCard';

/** Hero/quad groups per row; the group size is derived so exactly this many fill the content width. */
const GROUPS_PER_ROW = 4;

export const FeaturedGridLayout = ({ data, isFetchingNextPage = false, layoutHeaderProps }: CasinoLayoutProps) => {
    const groupWidth = useCarouselCardSize(GROUPS_PER_ROW);
    const groupSize = Math.floor(groupWidth);
    const gridWidth = groupSize * GROUPS_PER_ROW + CARD_GAP * (GROUPS_PER_ROW - 1);

    if (!data || data.length === 0) {
        return null;
    }

    const groups = toCheckerboardGroups(data, GROUPS_PER_ROW);
    const groupsInLastRow = groups.length % GROUPS_PER_ROW;
    const skeletonCount = isFetchingNextPage ? GROUPS_PER_ROW - groupsInLastRow : 0;

    return (
        <Box>
            {layoutHeaderProps ? <LayoutHeader {...layoutHeaderProps} /> : null}
            <Box style={[styles.grid, { width: gridWidth }]}>
                {groups.map((group, index) => (
                    <TiledGroupCard key={tiledGroupKey(group, index)} group={group} groupSize={groupSize} />
                ))}
                {Array.from({ length: skeletonCount }, (_, index) => (
                    <Box
                        key={`skeleton-${index}`}
                        width={groupSize}
                        height={groupSize}
                        borderRadius="r20"
                        overflow="hidden"
                        backgroundColor={ds.colorToken.background.neutral.subtle}
                    >
                        <LoadingSkeleton
                            skeletonColor={designSystem.colors.neutral700}
                            height={groupSize}
                            width={groupSize}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: CARD_GAP,
    },
});
